import { Alert, Button, Card, Form, Space, message } from 'antd';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { CustomerDetail } from '../../api/customers';
import { ApiError } from '../../api/errors';

/** Tham số của một thẻ sửa nhóm thông tin khách hàng. */
export interface CustomerEditCardProps<TValues extends object> {
  /** Tiêu đề thẻ. */
  readonly title: string;

  /** Giải thích nhóm thông tin này dùng để làm gì. Hiện ngay dưới tiêu đề. */
  readonly description?: string;

  /** Người đang đăng nhập có quyền sửa nhóm thông tin này. */
  readonly canEdit: boolean;

  /** Giá trị hiện tại, dùng để điền vào biểu mẫu. */
  readonly initialValues: TValues;

  /** Gửi thay đổi lên máy chủ và trả về khách hàng sau khi sửa. */
  readonly onSave: (values: TValues) => Promise<CustomerDetail>;

  /** Nhận khách hàng sau khi sửa, để trang chi tiết cập nhật mọi thẻ khác. */
  readonly onSaved: (updated: CustomerDetail) => void;

  /** Các ô nhập của nhóm thông tin này. */
  readonly children: ReactNode;
}

/**
 * Một thẻ sửa một nhóm thông tin của khách hàng.
 *
 * Khuôn dùng chung cho sáu nhóm thông tin, và nó tồn tại vì sáu nhóm đó có đúng cùng một vòng đời:
 * điền giá trị hiện tại, cho sửa nếu có quyền, gửi lên, hiện thông báo, cập nhật lại toàn trang.
 * Sao chép vòng đời đó sáu lần thì chỗ dễ bỏ sót nhất là bước cuối — cập nhật lại toàn trang thay
 * vì chỉ cập nhật thẻ vừa lưu. Bỏ sót bước đó làm những thẻ khác hiển thị dữ liệu cũ, và người dùng
 * chỉ phát hiện khi tải lại trang.
 *
 * **Không có quyền sửa thì các ô vẫn hiện, chỉ bị vô hiệu hóa.** Ẩn hẳn sẽ làm người xem không biết
 * thông tin đó có tồn tại, và họ sẽ đi hỏi hoặc khai trùng vào một chỗ khác.
 */
export function CustomerEditCard<TValues extends object>({
  title,
  description,
  canEdit,
  initialValues,
  onSave,
  onSaved,
  children,
}: CustomerEditCardProps<TValues>): ReactNode {
  const [form] = Form.useForm<TValues>();
  const [isSaving, setIsSaving] = useState(false);

  /*
    Điền lại biểu mẫu mỗi khi giá trị từ máy chủ đổi. Cần thiết vì một thẻ khác lưu xong sẽ làm cả
    trang nhận dữ liệu mới, và nếu thẻ này giữ nguyên giá trị cũ trong ô thì lần lưu tiếp theo của nó
    sẽ ghi ngược dữ liệu cũ lên dữ liệu mới.
  */
  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  async function handleSubmit(values: TValues): Promise<void> {
    setIsSaving(true);

    try {
      const updated = await onSave(values);

      void message.success('Đã lưu thay đổi.');
      onSaved(updated);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được. Thử lại sau.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card size="small" title={title}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {description !== undefined && <Alert type="info" showIcon message={description} />}

        {!canEdit && (
          <Alert
            type="warning"
            showIcon
            message="Bạn không có quyền sửa nhóm thông tin này"
            description="Các ô dưới đây chỉ để xem. Liên hệ người quản trị nếu cần sửa."
          />
        )}

        <Form<TValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={initialValues}
          disabled={!canEdit || isSaving}
          onFinish={(values) => {
            void handleSubmit(values);
          }}
        >
          {children}

          {canEdit && (
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={isSaving}>
                Lưu
              </Button>
            </Form.Item>
          )}
        </Form>
      </Space>
    </Card>
  );
}
